import type { ApiResponse } from '@safetyhub/shared';
import api from './api';

/**
 * **법정 의무 ↔ 양식 링크** API 클라이언트 — L24(US-0723) 링크 저작 입구. 카탈로그 화면의 「양식」 모달이 쓴다.
 *
 * base `/admin/legal-obligations`. 계약 정본은 서버 `AdminLegalObligationsController`의 L24 라우트 JSDoc과
 * `ObligationTemplateLinkService`다(판정 셋: 양식 실재 · 한 양식 한 역할 · 정본(PRIMARY)은 의무당 하나).
 * 권한은 카탈로그와 같은 `legal-obligation-catalog`다.
 *
 * ⚠️ 해제는 **행을 지우지 않는다** — `isCurrent=false`로 이력에 남는다(그래서 목록에 해제된 행이 함께 온다).
 * ⚠️ 응답 타입을 shared에 두지 않았다 — 소비처가 이 화면 하나라 shared 빌드 축을 늘리지 않는다(선례: 같은
 *    폴더 `legalObligationCatalog.ts`의 `UpdateLegalObligationResponse`).
 */

/** 서버 `ObligationTemplateLinkRole` 사본 */
export type ObligationTemplateLinkRole = 'PRIMARY' | 'ALTERNATIVE' | 'PARTIAL' | 'SUMMARY';

/** 역할 표시 라벨 — 순서가 선택지 순서다 */
export const OBLIGATION_TEMPLATE_LINK_ROLE_LABEL: Record<ObligationTemplateLinkRole, string> = {
  PRIMARY: '정본',
  ALTERNATIVE: '대체 가능',
  PARTIAL: '일부만 충족',
  SUMMARY: '집계',
};

/** 서버 `ObligationTemplateLinkRow` — 날짜는 JSON이라 문자열이다 */
export interface ObligationTemplateLink {
  id: string;
  templateKey: string;
  /** 양식이 지워졌으면 null */
  templateName: string | null;
  templateEnabled: boolean;
  role: ObligationTemplateLinkRole;
  isCurrent: boolean;
  linkedAt: string;
  unlinkedAt: string | null;
  note: string | null;
}

export interface ObligationTemplateOption {
  key: string;
  name: string;
  enabled: boolean;
}

function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) {
    throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  }
  return envelope.data;
}

const BASE = '/admin/legal-obligations';

export const OBLIGATION_TEMPLATE_LINK_QUERY_KEYS = {
  links: (obligationId: string) => ['admin', 'legal-obligations', obligationId, 'template-links'] as const,
  options: ['admin', 'legal-obligations', 'template-options'] as const,
};

export const obligationTemplateLinksApi = {
  async list(obligationId: string): Promise<ObligationTemplateLink[]> {
    const res = await api.get<ApiResponse<ObligationTemplateLink[]>>(`${BASE}/${obligationId}/template-links`);
    return unwrapEnvelope(res.data);
  },

  async options(): Promise<ObligationTemplateOption[]> {
    const res = await api.get<ApiResponse<ObligationTemplateOption[]>>(`${BASE}/template-options`);
    return unwrapEnvelope(res.data);
  },

  async link(
    obligationId: string,
    body: { templateKey: string; role: ObligationTemplateLinkRole; note?: string | null },
  ): Promise<ObligationTemplateLink> {
    const res = await api.post<ApiResponse<ObligationTemplateLink>>(`${BASE}/${obligationId}/template-links`, body);
    return unwrapEnvelope(res.data);
  },

  async unlink(obligationId: string, linkId: string): Promise<ObligationTemplateLink> {
    const res = await api.delete<ApiResponse<ObligationTemplateLink>>(
      `${BASE}/${obligationId}/template-links/${linkId}`,
    );
    return unwrapEnvelope(res.data);
  },
};
